import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];


const SECTION_LABELS = {
  recurring:     'Recurring Monthly Expense',
  miscellaneous: 'Miscellaneous',
  other_claims:  'Other Claims',
  invoices:      'Additional Invoices (AWS / OE Coding / Others)',
};
const SECTION_ORDER = ['recurring', 'miscellaneous', 'other_claims', 'invoices'];

const HIGHLIGHT_STYLE = {
  '':       '#fff',
  yellow:   '#fffde7',
  orange:   '#fff3e0',
};

const token = () => localStorage.getItem('token');
const authHeader = () => ({ Authorization: `Token ${token()}` });

function CommentsPanel({ reportId }) {
  const [comments, setComments] = useState([]);

  const load = () => {
    if (!reportId) return;
    axios.get(`/api/expenses/reports/${reportId}/comments/`, { headers: authHeader() })
      .then(r => setComments(r.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [reportId]);

  const deleteComment = async (commentId) => {
    try {
      await axios.delete(`/api/expenses/reports/${reportId}/comments/${commentId}/`, { headers: authHeader() });
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch { /* ignore */ }
  };

  if (!comments.length) return null;

  const fmtTime = d => new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{ marginTop: 20, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '16px 20px' }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#333', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        💬 Comments from viewers <span style={{ background: '#e53935', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11 }}>{comments.length}</span>
      </div>
      {comments.map(c => (
        <div key={c.id} style={{ borderLeft: '3px solid #2e7d32', paddingLeft: 12, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#2e7d32' }}>{c.author_name}</span>
              <span style={{ fontSize: 11, color: '#aaa' }}>{fmtTime(c.created_at)}</span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#444', lineHeight: 1.6 }}>{c.text}</p>
          </div>
          <button
            onClick={() => deleteComment(c.id)}
            title="Delete comment"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 14, padding: '2px 4px', flexShrink: 0, lineHeight: 1 }}
            onMouseOver={e => e.currentTarget.style.color = '#e53935'}
            onMouseOut={e => e.currentTarget.style.color = '#bbb'}
          >✕</button>
        </div>
      ))}
    </div>
  );
}

export default function ExpenseEdit() {
  const { year, month } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [tally, setTally] = useState({});
  const [header, setHeader] = useState({ name: '', department: '', branch: '', start_date: '', end_date: '' });
  const [invoices, setInvoices] = useState([]);
  const [prevMonth, setPrevMonth] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('items');
  const fileInputRef = useRef();
  const autoSaveTimer = useRef(null);
  const initialized = useRef(false);
  const monthName = MONTHS[(parseInt(month) || 1) - 1];

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadOrInit = useCallback(async () => {
    try {
      const res = await axios.get(
        `/api/expenses/reports/by-month/${year}/${month}/`,
        { headers: authHeader() }
      );
      applyReport(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        // init
        try {
          const res2 = await axios.post(
            `/api/expenses/reports/by-month/${year}/${month}/init/`,
            {},
            { headers: authHeader() }
          );
          applyReport(res2.data);
        } catch {
          showToast('Failed to initialise report.', 'error');
        }
      }
    }
  }, [year, month]);

  function applyReport(data) {
    initialized.current = false;  // suppress auto-save during state updates
    setReport(data);
    setHeader({
      name: data.name || '',
      department: data.department || '',
      branch: data.branch || '',
      start_date: data.start_date || '',
      end_date: data.end_date || '',
    });
    setTally(data.tally_data || {});
    setLineItems(data.line_items || []);
    setInvoices(data.invoices || []);
    if (data.prev_month) setPrevMonth(data.prev_month);
    setTimeout(() => { initialized.current = true; }, 100);
  }

  useEffect(() => { loadOrInit(); }, [loadOrInit]);

  function updateItem(id, field, value) {
    setLineItems(prev => prev.map(it => it.id === id ? { ...it, [field]: value } : it));
  }

  function addRow(section) {
    const sectionItems = lineItems.filter(it => it.section === section);
    const maxOrder = sectionItems.reduce((m, it) => Math.max(m, it.order || 0), 0);
    setLineItems(prev => [...prev, {
      id: `new_${Date.now()}`, section, order: maxOrder + 1,
      description: '', quantity: '', approximate_amount: '', actual_amount: '',
      additional_comment: '', highlight_color: '', is_preset: false,
    }]);
  }

  async function removeRow(itemId) {
    if (String(itemId).startsWith('new_')) {
      setLineItems(prev => prev.filter(it => it.id !== itemId));
      return;
    }
    try {
      // Unpin first so _seed_line_items won't re-create it on next load
      const item = lineItems.find(it => it.id === itemId);
      if (item?.is_preset) {
        await axios.post(
          `/api/expenses/reports/${report.id}/items/${itemId}/toggle-pin/`,
          {},
          { headers: authHeader() }
        );
      }
      await axios.delete(
        `/api/expenses/reports/${report.id}/items/${itemId}/`,
        { headers: authHeader() }
      );
      setLineItems(prev => prev.filter(it => it.id !== itemId));
    } catch {
      showToast('Could not remove row.', 'error');
    }
  }

  function buildTallyWithComputed() {
    // Items credited to account: non-vendor tagged items across all sections
    const creditedItems   = lineItems.filter(it => it.payment_type !== 'vendor');
    const toReceive       = creditedItems.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
    // Items paid directly to vendor: any item tagged vendor (invoices section OR recurring/misc/other)
    const vendorItems     = lineItems.filter(it => it.payment_type === 'vendor');
    const vendorTotal     = vendorItems.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
    const grandTotal      = toReceive + vendorTotal;
    // Office expense = non-invoice items credited to account (for display breakdown)
    const officeExpense   = lineItems.filter(it => it.section !== 'invoices' && it.payment_type !== 'vendor')
                              .reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
    const internalInv     = lineItems.filter(it => it.section === 'invoices' && it.payment_type !== 'vendor')
                              .reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
    const actualExpenseTotal = lineItems.reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
    const amountReceived = parseFloat(tally.amount_received) || 0;
    return {
      ...tally,
      office_expense: officeExpense,
      grand_total: grandTotal,
      total_estimated_office_expense: toReceive,
      other_invoices: vendorTotal,
      total_estimated_expenses: grandTotal,
      actual_expense: actualExpenseTotal,
      extra_spent: actualExpenseTotal - amountReceived,
    };
  }

  function buildPayload(currentTally) {
    return {
      ...header,
      tally_data: currentTally ?? buildTallyWithComputed(),
      line_items: lineItems.map(it => ({
        ...(String(it.id).startsWith('new_') ? {} : { id: it.id }),
        section: it.section,
        order: it.order,
        description: it.description,
        quantity: it.quantity,
        approximate_amount: it.approximate_amount || null,
        actual_amount: it.actual_amount || null,
        additional_comment: it.additional_comment,
        highlight_color: it.highlight_color,
        payment_type: it.payment_type || '',
        is_preset: it.is_preset || false,
      })),
    };
  }

  const doSave = useCallback(async () => {
    if (!report) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `/api/expenses/reports/${report.id}/save/`,
        buildPayload(),
        { headers: authHeader() }
      );
      applyReport(res.data);
    } catch {
      showToast('Auto-save failed.', 'error');
    } finally {
      setSaving(false);
    }
  }, [report, lineItems, tally, header]); // eslint-disable-line

  // Auto-save 1.5 s after any change (skip on initial data load)
  useEffect(() => {
    if (!initialized.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(doSave, 1500);
    return () => clearTimeout(autoSaveTimer.current);
  }, [lineItems, tally, header]); // eslint-disable-line

  async function handleSubmit() {
    if (!report) return;
    // flush any pending auto-save first
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
    setSaving(true);
    try {
      await axios.patch(
        `/api/expenses/reports/${report.id}/save/`,
        buildPayload(),
        { headers: authHeader() }
      );
    } catch {
      showToast('Could not save before submitting.', 'error');
      setSaving(false);
      return;
    }
    setSaving(false);
    setSubmitting(true);
    try {
      await axios.post(
        `/api/expenses/reports/${report.id}/submit/`,
        {},
        { headers: authHeader() }
      );
      showToast('Report submitted and email sent!');
    } catch {
      showToast('Submit failed. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function togglePin(itemId) {
    if (String(itemId).startsWith('new_')) {
      showToast('Save the row first before pinning.', 'error');
      return;
    }
    try {
      const res = await axios.post(
        `/api/expenses/reports/${report.id}/items/${itemId}/toggle-pin/`,
        {},
        { headers: authHeader() }
      );
      setLineItems(prev => prev.map(it =>
        it.id === itemId ? { ...it, is_preset: res.data.is_preset } : it
      ));
    } catch {
      showToast('Could not update pin.', 'error');
    }
  }

  async function handleReset() {
    if (!report) return;
    if (!window.confirm('Reset all line items to defaults? This will clear all amounts and remove custom rows. This cannot be undone.')) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `/api/expenses/reports/${report.id}/reset-items/`,
        {},
        { headers: authHeader() }
      );
      applyReport(res.data);
      showToast('Line items reset to defaults.');
    } catch {
      showToast('Reset failed. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleInvoiceUpload(e) {
    const file = e.target.files[0];
    if (!file || !report) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(
        `/api/expenses/reports/${report.id}/invoices/`,
        fd,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      setInvoices(prev => [...prev, res.data]);
      showToast('Invoice uploaded.');
    } catch {
      showToast('Upload failed.', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleInvoiceDelete(invoiceId) {
    if (!report) return;
    try {
      await axios.delete(
        `/api/expenses/reports/${report.id}/invoices/${invoiceId}/`,
        { headers: authHeader() }
      );
      setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
      showToast('Invoice removed.');
    } catch {
      showToast('Could not delete invoice.', 'error');
    }
  }

  const itemsBySection = {};
  SECTION_ORDER.forEach(s => { itemsBySection[s] = []; });
  lineItems.forEach(it => {
    if (itemsBySection[it.section]) itemsBySection[it.section].push(it);
  });

  const SECTION_HEADER_COLOR = {
    recurring:     { bg: '#e8f5e9', border: '#4caf50', label: '#2e7d32' },
    miscellaneous: { bg: '#e3f2fd', border: '#1976d2', label: '#1565c0' },
    other_claims:  { bg: '#e3f2fd', border: '#1976d2', label: '#1565c0' },
    invoices:      { bg: '#fff3e0', border: '#ff9800', label: '#e65100' },
  };

  if (!report) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f6f9' }}>
      <div style={{ color: '#aaa', fontSize: 15 }}>Loading…</div>
    </div>
  );

  const shareUrl = report.share_token
    ? `${window.location.origin}/expenses/view/${report.share_token}`
    : null;

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', fontFamily: 'Inter,Arial,sans-serif' }}>

      {/* Top bar */}
      <div style={{
        background: '#0f172a', color: '#fff',
        padding: '0 24px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', height: 56, position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => navigate('/expenses')}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontSize: 22, lineHeight: 1 }}
            title="Back to list"
          >←</button>
          <div>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{monthName} {year}</span>
            <span style={{ opacity: 0.65, fontSize: 12, marginLeft: 10 }}>Monthly Expense Report</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {shareUrl && (
            <button
              onClick={() => { navigator.clipboard?.writeText(shareUrl); showToast('Share link copied!'); }}
              style={btnStyle('#fff3', '#fff')}
              title="Copy shareable link"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share link
            </button>
          )}
          {saving && (
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              Saving…
            </span>
          )}
          <button
            onClick={handleReset}
            disabled={saving}
            style={btnStyle('rgba(255,255,255,0.15)', '#fff')}
          >
            ↺ Reset
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || saving}
            style={btnStyle('#43a047', '#fff')}
          >
            {submitting ? 'Sending…' : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                Submit &amp; Email
              </>
            )}
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 999,
          background: toast.type === 'error' ? '#d32f2f' : '#2e7d32',
          color: '#fff', padding: '12px 20px', borderRadius: 8,
          boxShadow: '0 4px 18px rgba(0,0,0,0.18)', fontSize: 13, fontWeight: 500,
          animation: 'fadeIn 0.2s',
        }}>
          {toast.msg}
        </div>
      )}

      <div style={{ maxWidth: 960, margin: '28px auto', padding: '0 16px' }}>

        {/* Header card */}
        <div style={card}>
          <div style={cardTitle}>Report Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
            {[
              { key: 'name', label: 'Name' },
              { key: 'department', label: 'Department' },
              { key: 'branch', label: 'Branch / Location' },
              { key: 'start_date', label: 'Start Date', type: 'date' },
              { key: 'end_date', label: 'End Date', type: 'date' },
            ].map(({ key, label, type }) => (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input
                  type={type || 'text'}
                  value={header[key] || ''}
                  onChange={e => setHeader(prev => ({ ...prev, [key]: e.target.value }))}
                  style={inputStyle}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: '2px solid #e0e0e0' }}>
          {[['items','Expense Items'], ['tally','Tally / Summary'], ['invoices','Invoices']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: '10px 22px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: activeTab === key ? 700 : 500,
                color: activeTab === key ? '#2e7d32' : '#666',
                borderBottom: activeTab === key ? '2.5px solid #2e7d32' : '2.5px solid transparent',
                marginBottom: -2, transition: 'color 0.15s',
              }}
            >{label}</button>
          ))}
        </div>

        {/* Line items tab */}
        {activeTab === 'items' && (() => {
          // Office items only (exclude invoices section) for the totals row label clarity
          const totalApprox = lineItems.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
          const totalActual = lineItems.reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
          const fmtN = v => v ? `₹${v.toLocaleString('en-IN')}` : '—';
          // Carry-forward from previous month
          const _prevItems = prevMonth?.line_items || [];
          const _prevActual = _prevItems.reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
          const _prevReceived = parseFloat(prevMonth?.tally_data?.amount_received) || 0;
          const _prevEstimated = parseFloat(prevMonth?.tally_data?.grand_total) || 0;
          const _prevBaseline = _prevReceived || _prevEstimated;
          const _prevCarryForward = (prevMonth && _prevActual > 0) ? _prevActual - _prevBaseline : 0;
          const _prevMonthName = prevMonth ? MONTHS[(prevMonth.month || 1) - 1] : '';
          const _netToRequest = totalApprox + _prevCarryForward;
          return (
          <div style={{ ...card, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            {SECTION_ORDER.map(section => {
              const { bg, border, label: secLabel } = SECTION_HEADER_COLOR[section];
              const items = itemsBySection[section];
              return (
                <div key={section} style={{ marginBottom: 24 }}>
                  <div style={{
                    background: bg, borderLeft: `4px solid ${border}`,
                    padding: '8px 14px', borderRadius: '6px 6px 0 0',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: secLabel }}>{SECTION_LABELS[section]}</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
                      <colgroup>
                        <col />
                        {section !== 'invoices' && <col style={{ width: 70 }} />}
                        <col style={{ width: 120 }} />
                        <col style={{ width: 120 }} />
                        <col />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 34 }} />
                        <col style={{ width: 46 }} />
                        <col style={{ width: 30 }} />
                      </colgroup>
                      <thead>
                        <tr style={{ background: '#fafafa' }}>
                          <th style={th}>Description</th>
                          {section !== 'invoices' && <th style={{ ...th, textAlign: 'center' }}>Qty</th>}
                          <th style={{ ...th, textAlign: 'right' }}>Approx Amount</th>
                          <th style={{ ...th, textAlign: 'right' }}>Actual Amount</th>
                          <th style={th}>Comment</th>
                          <th style={{ ...th, textAlign: 'center' }}>Type</th>
                          <th style={{ ...th, textAlign: 'center' }} title="Pin to future months">📌</th>
                          <th style={{ ...th, textAlign: 'center' }}>Color</th>
                          <th style={{ ...th }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(it => (
                          <tr key={it.id} style={{ background: HIGHLIGHT_STYLE[it.highlight_color] || '#fff' }}>
                            <td style={td2}>
                              <input
                                value={it.description || ''}
                                onChange={e => updateItem(it.id, 'description', e.target.value)}
                                style={cellInput}
                                placeholder="Description…"
                              />
                            </td>
                            {section !== 'invoices' && (
                              <td style={td2}>
                                <input
                                  value={it.quantity || ''}
                                  onChange={e => updateItem(it.id, 'quantity', e.target.value)}
                                  style={{ ...cellInput, textAlign: 'center' }}
                                />
                              </td>
                            )}
                            <td style={{ ...td2, textAlign: 'right' }}>
                              <input
                                type="number"
                                value={it.approximate_amount != null && it.approximate_amount !== '' ? +it.approximate_amount : ''}
                                onChange={e => updateItem(it.id, 'approximate_amount', e.target.value)}
                                style={{ ...cellInput, textAlign: 'right' }}
                                placeholder="0"
                              />
                            </td>
                            <td style={{ ...td2, textAlign: 'right' }}>
                              <input
                                type="number"
                                value={it.actual_amount != null && it.actual_amount !== '' ? +it.actual_amount : ''}
                                onChange={e => updateItem(it.id, 'actual_amount', e.target.value)}
                                style={{ ...cellInput, textAlign: 'right' }}
                                placeholder="0"
                              />
                            </td>
                            <td style={td2}>
                              <input
                                value={it.additional_comment || ''}
                                onChange={e => updateItem(it.id, 'additional_comment', e.target.value)}
                                style={cellInput}
                              />
                            </td>
                            <td style={{ ...td2, textAlign: 'center', verticalAlign: 'middle' }}>
                              <button
                                onClick={() => {
                                  const next = section === 'invoices'
                                    ? (it.payment_type === 'internal' ? 'vendor' : 'internal')
                                    : (it.payment_type === 'vendor' ? '' : 'vendor');
                                  updateItem(it.id, 'payment_type', next);
                                }}
                                style={{
                                  padding: '3px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer', border: 'none',
                                  fontWeight: 600, whiteSpace: 'nowrap',
                                  background: it.payment_type === 'vendor' ? '#fff3e0'
                                    : it.payment_type === 'internal' ? '#e8f5e9' : '#f1f8e9',
                                  color: it.payment_type === 'vendor' ? '#e65100'
                                    : it.payment_type === 'internal' ? '#2e7d32' : '#558b2f',
                                }}
                              >
                                {it.payment_type === 'vendor' ? '→ Vendor'
                                  : it.payment_type === 'internal' ? '← Acct'
                                  : '← Acct'}
                              </button>
                            </td>
                            <td style={{ ...td2, textAlign: 'center' }}>
                              <button
                                onClick={() => togglePin(it.id)}
                                title={it.is_preset ? 'Pinned — appears in future months. Click to unpin.' : 'Pin this row to future months'}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  fontSize: 14, opacity: it.is_preset ? 1 : 0.25,
                                  filter: it.is_preset ? 'none' : 'grayscale(1)',
                                  padding: '2px 4px',
                                }}
                              >📌</button>
                            </td>
                            <td style={{ ...td2, textAlign: 'center' }}>
                              <select
                                value={it.highlight_color || ''}
                                onChange={e => updateItem(it.id, 'highlight_color', e.target.value)}
                                style={{
                                  border: 'none', borderRadius: 4, padding: '2px 4px',
                                  background: HIGHLIGHT_STYLE[it.highlight_color] || 'transparent',
                                  fontSize: 11, cursor: 'pointer',
                                }}
                                title="Row colour"
                              >
                                <option value="">—</option>
                                <option value="yellow">🟡</option>
                                <option value="orange">🟠</option>
                              </select>
                            </td>
                            <td style={{ ...td2, textAlign: 'center', width: 28 }}>
                              <button
                                onClick={() => removeRow(it.id)}
                                title="Remove row"
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  fontSize: 15, color: '#e53935', padding: '2px 4px', lineHeight: 1,
                                }}
                              >✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    onClick={() => addRow(section)}
                    style={{
                      marginTop: 6, padding: '5px 14px', fontSize: 12, border: `1px dashed ${border}`,
                      color: secLabel, background: 'transparent', borderRadius: 6, cursor: 'pointer',
                    }}
                  >+ Add row</button>
                </div>
              );
            })}

            {/* Grand total row — table-based so columns align with section tables above */}
            <div style={{ overflowX: 'auto', marginTop: 8, border: '2px solid #4caf50', borderRadius: 8, background: '#e8f5e9', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 120 }} />
                  <col />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 34 }} />
                  <col style={{ width: 46 }} />
                  <col style={{ width: 30 }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13, color: '#1b5e20' }}>
                      Total (Estimated Expense)
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: 14, color: '#1b5e20', borderLeft: '1px solid #a5d6a7' }}>
                      {fmtN(totalApprox)}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: 14, color: '#1b5e20', borderLeft: '1px solid #a5d6a7' }}>
                      {fmtN(totalActual)}
                    </td>
                    <td colSpan={5} />
                  </tr>
                  {_prevCarryForward !== 0 && (
                    <tr style={{ background: _prevCarryForward > 0 ? '#fff3e0' : '#f1f8f1', borderTop: '1px solid #c8e6c9' }}>
                      <td style={{ padding: '8px 14px', fontSize: 12.5, color: _prevCarryForward > 0 ? '#e65100' : '#2e7d32', fontWeight: 600 }}>
                        {_prevMonthName} {_prevCarryForward > 0 ? `Extra Spent (added to ${monthName} bill)` : `Surplus (deducted from ${monthName} bill)`}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: _prevCarryForward > 0 ? '#e65100' : '#2e7d32', borderLeft: '1px solid #a5d6a7' }}>
                        {_prevCarryForward > 0 ? '+' : '−'} ₹{Math.abs(_prevCarryForward).toLocaleString('en-IN')}
                      </td>
                      <td colSpan={6} />
                    </tr>
                  )}
                  {_prevCarryForward !== 0 && (
                    <tr style={{ background: '#fce4ec', borderTop: '2px solid #e91e63' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13, color: '#880e4f' }}>
                        Net Amount to Request ({monthName})
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: 14, color: '#880e4f', borderLeft: '1px solid #f48fb1' }}>
                        ₹{_netToRequest.toLocaleString('en-IN')}
                      </td>
                      <td colSpan={6} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
          );
        })()}

        {/* Tally tab */}
        {activeTab === 'tally' && (() => {
          // Items credited to account = everything NOT tagged as vendor
          const toReceive      = lineItems.filter(it => it.payment_type !== 'vendor')
                                  .reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
          // All vendor-tagged items (any section: recurring, misc, invoices…)
          const vendorTotal    = lineItems.filter(it => it.payment_type === 'vendor')
                                  .reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
          const grandTotal     = toReceive + vendorTotal;
          // Sub-breakdown for display
          const officeExpense  = lineItems.filter(it => it.section !== 'invoices' && it.payment_type !== 'vendor')
                                  .reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
          const internalInv    = lineItems.filter(it => it.section === 'invoices' && it.payment_type !== 'vendor')
                                  .reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
          const totalEstExp    = grandTotal;
          // Sum of actual amounts entered in line items
          const actualExpenseTotal = lineItems.reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
          // Extra spent = actual expense minus amount received (positive = overspent, negative = surplus)
          const amountReceived = parseFloat(tally.amount_received) || 0;
          const extraSpent = actualExpenseTotal - amountReceived;

          // Previous month carry-forward (computed here so Reconciliation section can use it)
          const prevItems = prevMonth?.line_items || [];
          const prevActualTotal = prevItems.reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
          const prevAmountReceived = parseFloat(prevMonth?.tally_data?.amount_received) || 0;
          // Extra spent = actual - (amount received if entered, else estimated grand total)
          const prevEstimatedTotal = parseFloat(prevMonth?.tally_data?.grand_total) || 0;
          const prevBaseline = prevAmountReceived || prevEstimatedTotal;
          const prevCarryForward = (prevMonth && prevActualTotal > 0) ? prevActualTotal - prevBaseline : 0;
          // Net amount to request this month = this month's credited items ± last month's balance
          const netToRequest = toReceive + prevCarryForward;

          const fmtN = v => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—';

          const numInput = (key) => (
            <input
              type="number"
              value={tally[key] ?? ''}
              onChange={e => setTally(prev => ({ ...prev, [key]: e.target.value }))}
              style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right',
                fontSize: 13, fontWeight: 600, outline: 'none', padding: '2px 0', color: 'inherit' }}
              placeholder="0"
            />
          );

          const Auto = ({ val, bg = '#e8f5e9', color = '#1b5e20' }) => (
            <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', textAlign: 'right',
              fontWeight: 700, fontSize: 13, background: bg, color, minWidth: 130 }}>
              {fmtN(val)}
            </td>
          );
          const Input = ({ children, bg = '#fff' }) => (
            <td style={{ padding: '5px 10px', borderBottom: '1px solid #eee', background: bg, minWidth: 130 }}>
              {children}
            </td>
          );
          const Label = ({ children, sub, bg }) => (
            <td style={{ padding: '8px 16px', borderBottom: '1px solid #eee', fontSize: 13,
              color: sub ? '#777' : '#333', fontStyle: sub ? 'italic' : 'normal',
              background: bg, paddingLeft: sub ? 28 : 16 }}>
              {children}
            </td>
          );
          const Head = ({ children, bg = '#388e3c', sub }) => (
            <tr>
              <td colSpan={2} style={{
                background: sub ? '#f5f5f5' : bg, color: sub ? '#555' : '#fff',
                fontWeight: 700, fontSize: sub ? 11 : 12,
                padding: sub ? '6px 16px' : '9px 16px',
                letterSpacing: 0.4, borderBottom: '1px solid #ddd',
                textTransform: sub ? 'none' : 'uppercase',
              }}>{children}</td>
            </tr>
          );

          return (
            <div style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>

              {/* Previous month summary */}
              {prevMonth && (() => {
                const pm = prevMonth;
                const pt = pm.tally_data || {};
                const pItems = pm.line_items || [];
                const pmName = MONTHS[(pm.month || 1) - 1];
                const pOffice   = pItems.filter(it => it.section !== 'invoices' && it.payment_type !== 'vendor' && it.description);
                const pInternal = pItems.filter(it => it.section === 'invoices' && it.payment_type !== 'vendor' && it.description);
                const pVendor   = pItems.filter(it => it.payment_type === 'vendor' && it.description);
                const pOfficeT   = pOffice.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
                const pInternalT = pInternal.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
                const pVendorT   = pVendor.reduce((s, it) => s + (parseFloat(it.approximate_amount) || 0), 0);
                const pTotal = pOfficeT + pInternalT + pVendorT;
                const pActualTotal = pItems.reduce((s, it) => s + (parseFloat(it.actual_amount) || 0), 0);
                const pAmountReceived = parseFloat(pt.amount_received) || 0;
                const pExtraSpent = pActualTotal - pAmountReceived;
                const SubRow = ({ label, val, indent }) => val ? (
                  <tr style={{ background: '#fafafa' }}>
                    <td style={{ padding: '5px 16px 5px', paddingLeft: indent ? 32 : 16, fontSize: 12, color: '#666', borderBottom: '1px solid #f0f0f0' }}>{indent ? '↳ ' : ''}{label}</td>
                    <td style={{ padding: '5px 12px', fontSize: 12, color: '#444', textAlign: 'right', borderBottom: '1px solid #f0f0f0', fontVariantNumeric: 'tabular-nums' }}>{fmtN(val)}</td>
                  </tr>
                ) : null;
                return (
                  <div style={{ ...card, marginBottom: 12, padding: 0, overflow: 'hidden', border: '1px solid #cfd8dc' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <colgroup><col style={{ width: '68%' }}/><col style={{ width: '32%' }}/></colgroup>
                      <tbody>
                        <tr><td colSpan={2} style={{ background: '#546e7a', color: '#fff', fontWeight: 700, fontSize: 12, padding: '9px 16px', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                          {pmName} {pm.year} — Previous Month Tally
                        </td></tr>

                        {/* Breakdown sub-rows */}
                        <tr style={{ background: '#f5f5f5' }}>
                          <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#333', borderBottom: '1px solid #eee' }}>Office Expense</td>
                          <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 700, color: '#333', textAlign: 'right', borderBottom: '1px solid #eee' }}>{fmtN(pOfficeT)}</td>
                        </tr>
                        {pOffice.map(it => <SubRow key={it.id} label={it.description} val={it.approximate_amount} indent />)}

                        <tr style={{ background: '#f5f5f5' }}>
                          <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#333', borderBottom: '1px solid #eee' }}>Internal Transfer Invoices</td>
                          <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 700, color: '#333', textAlign: 'right', borderBottom: '1px solid #eee' }}>{fmtN(pInternalT)}</td>
                        </tr>
                        {pInternal.map(it => <SubRow key={it.id} label={it.description} val={it.approximate_amount} indent />)}

                        <tr style={{ background: '#f5f5f5' }}>
                          <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#333', borderBottom: '1px solid #eee' }}>Direct Vendor Payments</td>
                          <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 700, color: '#333', textAlign: 'right', borderBottom: '1px solid #eee' }}>{fmtN(pVendorT)}</td>
                        </tr>
                        {pVendor.map(it => <SubRow key={it.id} label={it.description} val={it.approximate_amount} indent />)}

                        <tr style={{ background: '#eceff1', borderTop: '2px solid #546e7a' }}>
                          <td style={{ padding: '9px 16px', fontWeight: 700, fontSize: 13, color: '#263238', borderBottom: '1px solid #eee' }}>Estimated Total</td>
                          <td style={{ padding: '9px 12px', fontWeight: 700, fontSize: 13, color: '#263238', textAlign: 'right', borderBottom: '1px solid #eee', fontVariantNumeric: 'tabular-nums' }}>{fmtN(pTotal)}</td>
                        </tr>

                        {/* Actual expense and reconciliation from previous month */}
                        {pActualTotal > 0 && <tr>
                          <td style={{ padding: '7px 16px', fontSize: 13, color: '#555', borderBottom: '1px solid #f0f0f0' }}>Actual Expense</td>
                          <td style={{ padding: '7px 12px', fontSize: 13, fontWeight: 600, color: '#1565c0', textAlign: 'right', borderBottom: '1px solid #f0f0f0', fontVariantNumeric: 'tabular-nums' }}>{fmtN(pActualTotal)}</td>
                        </tr>}
                        {pAmountReceived > 0 && <tr>
                          <td style={{ padding: '7px 16px', fontSize: 13, color: '#555', borderBottom: '1px solid #f0f0f0' }}>Amount Received</td>
                          <td style={{ padding: '7px 12px', fontSize: 13, fontWeight: 600, color: '#2e7d32', textAlign: 'right', borderBottom: '1px solid #f0f0f0', fontVariantNumeric: 'tabular-nums' }}>{fmtN(pAmountReceived)}</td>
                        </tr>}
                        {pActualTotal > 0 && <tr style={{ background: pExtraSpent > 0 ? '#ffebee' : '#e8f5e9', borderTop: '2px solid ' + (pExtraSpent > 0 ? '#ef9a9a' : '#a5d6a7') }}>
                          <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: pExtraSpent > 0 ? '#b71c1c' : '#2e7d32', borderBottom: '1px solid #f0f0f0' }}>
                            {pExtraSpent > 0 ? `Extra Spent (added to ${pmName} bill)` : 'Surplus (carry forward)'}
                          </td>
                          <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 700, color: pExtraSpent > 0 ? '#b71c1c' : '#2e7d32', textAlign: 'right', borderBottom: '1px solid #f0f0f0', fontVariantNumeric: 'tabular-nums' }}>{fmtN(Math.abs(pExtraSpent))}</td>
                        </tr>}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              <div style={{ ...card, borderTopLeftRadius: 0, borderTopRightRadius: 0, padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '68%' }} />
                  <col style={{ width: '32%' }} />
                </colgroup>
                <tbody>

                  {/* Block 1: Monthly cost breakdown */}
                  <Head>Monthly Expense Breakdown</Head>

                  {/* Office Expense group */}
                  {(() => {
                    const items = lineItems.filter(it => it.section !== 'invoices' && it.payment_type !== 'vendor' && it.description);
                    return <>
                      <tr style={{ background: '#f5f5f5' }}>
                        <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#333', borderBottom: '1px solid #eee' }}>
                          Office Expense
                        </td>
                        <Auto val={officeExpense} bg="#f5f5f5" color="#333" />
                      </tr>
                      {items.map(it => (
                        <tr key={it.id} style={{ background: '#fafafa' }}>
                          <td style={{ padding: '5px 16px 5px 32px', fontSize: 12, color: '#666', borderBottom: '1px solid #f5f5f5' }}>
                            ↳ {it.description}
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, color: '#555', textAlign: 'right', borderBottom: '1px solid #f5f5f5', fontVariantNumeric: 'tabular-nums' }}>
                            {it.approximate_amount ? `₹${parseFloat(it.approximate_amount).toLocaleString('en-IN')}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </>;
                  })()}

                  {/* Internal Transfer Invoices group */}
                  {(() => {
                    const items = lineItems.filter(it => it.section === 'invoices' && it.payment_type !== 'vendor' && it.description);
                    return <>
                      <tr style={{ background: '#f5f5f5' }}>
                        <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#333', borderBottom: '1px solid #eee' }}>
                          Internal Transfer Invoices
                        </td>
                        <Auto val={internalInv} bg="#f5f5f5" color="#333" />
                      </tr>
                      {items.map(it => (
                        <tr key={it.id} style={{ background: '#fafafa' }}>
                          <td style={{ padding: '5px 16px 5px 32px', fontSize: 12, color: '#666', borderBottom: '1px solid #f5f5f5' }}>
                            ↳ {it.description}
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, color: '#555', textAlign: 'right', borderBottom: '1px solid #f5f5f5', fontVariantNumeric: 'tabular-nums' }}>
                            {it.approximate_amount ? `₹${parseFloat(it.approximate_amount).toLocaleString('en-IN')}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </>;
                  })()}

                  {/* Direct Vendor Payments group */}
                  {(() => {
                    const items = lineItems.filter(it => it.payment_type === 'vendor' && it.description);
                    return <>
                      <tr style={{ background: '#f5f5f5' }}>
                        <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#333', borderBottom: '1px solid #eee' }}>
                          Direct Vendor Payments
                        </td>
                        <Auto val={vendorTotal} bg="#f5f5f5" color="#333" />
                      </tr>
                      {items.map(it => (
                        <tr key={it.id} style={{ background: '#fafafa' }}>
                          <td style={{ padding: '5px 16px 5px 32px', fontSize: 12, color: '#666', borderBottom: '1px solid #f5f5f5' }}>
                            ↳ {it.description}
                          </td>
                          <td style={{ padding: '5px 12px', fontSize: 12, color: '#555', textAlign: 'right', borderBottom: '1px solid #f5f5f5', fontVariantNumeric: 'tabular-nums' }}>
                            {it.approximate_amount ? `₹${parseFloat(it.approximate_amount).toLocaleString('en-IN')}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </>;
                  })()}

                  <tr style={{ borderTop: '2px solid #4caf50' }}>
                    <Label><strong>Total Expected Expense (This Month)</strong></Label>
                    <Auto val={grandTotal} />
                  </tr>

                  {/* Block 2: Payment classification */}
                  <Head>Payment Classification</Head>
                  <tr>
                    <td style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, color: '#b71c1c', borderBottom: '1px solid #eee', background: '#ffebee' }}>
                      Credited to Account (DP Head)
                    </td>
                    <Auto val={toReceive} bg="#ffebee" color="#b71c1c" />
                  </tr>
                  <tr style={{ background: '#e3f2fd' }}>
                    <td style={{ padding: '8px 16px', fontWeight: 700, fontSize: 13, color: '#1565c0', borderBottom: '1px solid #eee' }}>
                      Direct Vendor Payments
                    </td>
                    <Auto val={vendorTotal} bg="#e3f2fd" color="#1565c0" />
                  </tr>
                  <tr style={{ background: '#fff3e0', borderTop: '2px solid #ff9800' }}>
                    <td style={{ padding: '8px 16px', fontWeight: 700, fontSize: 13, color: '#e65100', borderBottom: '1px solid #eee' }}>
                      Grand Total
                    </td>
                    <Auto val={totalEstExp} bg="#fff3e0" color="#e65100" />
                  </tr>

                  {/* Block 3: Reconciliation */}
                  <Head bg="#546e7a">Reconciliation — Amount to Request This Month</Head>

                  {/* This month's credited amount (base) */}
                  <tr>
                    <Label>This Month's Office Expense <span style={{ color: '#aaa', fontSize: 11 }}>(approx, non-vendor)</span></Label>
                    <Auto val={toReceive} bg="#ffebee" color="#b71c1c" />
                  </tr>

                  {/* Previous month carry-forward */}
                  {prevMonth && prevCarryForward !== 0 && (
                    <tr style={{ background: prevCarryForward > 0 ? '#fff3e0' : '#e8f5e9' }}>
                      <Label bg={prevCarryForward > 0 ? '#fff3e0' : '#e8f5e9'}>
                        {MONTHS[(prevMonth.month || 1) - 1]} {prevCarryForward > 0
                          ? `Extra Spent (added to ${monthName} bill)`
                          : `Surplus (deducted from ${monthName} bill)`}
                      </Label>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', textAlign: 'right',
                        fontWeight: 700, fontSize: 13, minWidth: 130,
                        background: prevCarryForward > 0 ? '#fff3e0' : '#e8f5e9',
                        color: prevCarryForward > 0 ? '#e65100' : '#2e7d32' }}>
                        {prevCarryForward > 0 ? '+' : '−'} {`₹${Math.abs(prevCarryForward).toLocaleString('en-IN')}`}
                      </td>
                    </tr>
                  )}

                  {/* Net amount to request */}
                  <tr style={{ background: '#fce4ec', borderTop: '2px solid #e91e63' }}>
                    <Label bg="#fce4ec">
                      <strong>Net Amount to Request This Month</strong>
                      {prevMonth && prevCarryForward !== 0 && (
                        <span style={{ color: '#aaa', fontSize: 11, marginLeft: 6 }}>
                          = This Month {prevCarryForward > 0 ? '+' : '−'} Carry-forward
                        </span>
                      )}
                    </Label>
                    <Auto val={netToRequest} bg="#fce4ec" color="#c2185b" />
                  </tr>

                  <tr>
                    <Label>Rent Transferred (Direct)</Label>
                    <Input><div style={{ display: 'flex', justifyContent: 'flex-end' }}>{numInput('rent_transferred')}</div></Input>
                  </tr>
                  <tr style={{ background: '#f0faf0' }}>
                    <Label bg="#f0faf0">Amount Received <span style={{ color: '#aaa', fontSize: 11 }}>(actual credit received this month)</span></Label>
                    <Input bg="#f0faf0"><div style={{ display: 'flex', justifyContent: 'flex-end', color: '#2e7d32' }}>{numInput('amount_received')}</div></Input>
                  </tr>
                  <tr style={{ background: '#fffde7' }}>
                    <Label bg="#fffde7">Actual Expense <span style={{ color: '#aaa', fontSize: 11 }}>(sum of actual amounts entered)</span></Label>
                    <Auto val={actualExpenseTotal} bg="#fffde7" color="#e65100" />
                  </tr>
                  <tr style={{ background: extraSpent > 0 ? '#ffebee' : '#e8f5e9', borderTop: '2px solid ' + (extraSpent > 0 ? '#ef9a9a' : '#a5d6a7') }}>
                    <Label bg={extraSpent > 0 ? '#ffebee' : '#e8f5e9'}>
                      <strong>{extraSpent > 0 ? 'Extra Spent (carries to next month)' : 'Surplus (carries to next month)'}</strong>
                      <span style={{ color: '#aaa', fontSize: 11, marginLeft: 6 }}>= Actual − Received</span>
                    </Label>
                    <Auto
                      val={Math.abs(extraSpent)}
                      bg={extraSpent > 0 ? '#ffebee' : '#e8f5e9'}
                      color={extraSpent > 0 ? '#b71c1c' : '#2e7d32'}
                    />
                  </tr>

                </tbody>
              </table>
              <div style={{ padding: '9px 16px', background: '#fafafa', borderTop: '1px solid #eee', fontSize: 11, color: '#aaa', display: 'flex', gap: 16 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, background: '#e8f5e9', borderRadius: 2, display: 'inline-block', border: '1px solid #4caf50' }}/>
                  Auto-calculated
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, background: '#fff', borderRadius: 2, display: 'inline-block', border: '1px solid #ddd' }}/>
                  Enter manually
                </span>
              </div>
            </div>
            </div>
          );
        })()}

        {/* Invoices tab */}
        {activeTab === 'invoices' && (
          <div style={{ ...card, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={cardTitle}>Invoices</div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={btnStyle('#2e7d32', '#fff')}
              >
                {uploading ? 'Uploading…' : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Upload Invoice
                  </>
                )}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleInvoiceUpload}
                style={{ display: 'none' }}
                accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.docx,.doc"
              />
            </div>
            {invoices.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#bbb', padding: 32, fontSize: 13 }}>No invoices uploaded yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {invoices.map(inv => (
                  <div key={inv.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', background: '#f7f8fa',
                    border: '1px solid #e8e8e8', borderRadius: 8,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <a href={inv.file} target="_blank" rel="noopener noreferrer"
                      style={{ flex: 1, color: '#1976d2', fontSize: 13, fontWeight: 500, textDecoration: 'none' }}>
                      {inv.original_filename}
                    </a>
                    {inv.description && <span style={{ color: '#999', fontSize: 12 }}>{inv.description}</span>}
                    <button
                      onClick={() => handleInvoiceDelete(inv.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53935', padding: 4 }}
                      title="Remove invoice"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Comments from public viewers */}
        <CommentsPanel reportId={report?.id} />

        {/* Share link display */}
        {shareUrl && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#f1f8e9', borderRadius: 8, border: '1px solid #c5e1a5', display: 'flex', alignItems: 'center', gap: 12, fontSize: 12 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2.2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            <span style={{ color: '#555' }}>Public share link:</span>
            <a href={shareUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2e7d32', fontWeight: 600, wordBreak: 'break-all' }}>{shareUrl}</a>
            <button
              onClick={() => { navigator.clipboard?.writeText(shareUrl); showToast('Copied!'); }}
              style={{ marginLeft: 'auto', background: 'none', border: '1px solid #c5e1a5', color: '#4caf50', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', fontSize: 11 }}
            >Copy</button>
          </div>
        )}

      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>
    </div>
  );
}

const card = {
  background: '#fff', borderRadius: 10, padding: '22px 24px',
  boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 16,
  border: '1px solid #e8e8e8',
};
const cardTitle = {
  fontSize: 14, fontWeight: 700, color: '#1a1a1a', marginBottom: 16,
};
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#888',
  textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5,
};
const inputStyle = {
  width: '100%', padding: '8px 11px', border: '1.5px solid #e0e0e0', borderRadius: 7,
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};
const cellInput = {
  width: '100%', border: 'none', background: 'transparent', padding: '4px 6px',
  fontSize: 13, outline: 'none', color: '#333',
};
const th = {
  padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 600,
  color: '#666', borderBottom: '1.5px solid #e8e8e8', background: '#fafafa',
};
const td2 = {
  padding: '4px 4px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle',
};
function btnStyle(bg, color) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 16px', borderRadius: 7, border: 'none', cursor: 'pointer',
    background: bg, color, fontSize: 13, fontWeight: 600,
    transition: 'opacity 0.15s',
  };
}
